import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import QRCode from "qrcode";
import { getQrToken } from "../../services/teacherAttendanceService";
import { useTranslation } from "react-i18next";

interface QrCodeDisplayProps {
  schoolId?: number;
}

export const QrCodeDisplay: React.FC<QrCodeDisplayProps> = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState<string>("");
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(300);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const clockIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch rotating QR token
  const fetchToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const tokenPayload = await getQrToken();
      setToken(tokenPayload);
      setCountdown(300); // Reset countdown to 300 seconds (5 minutes)
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_fetch_token") || "Failed to fetch QR token");
    } finally {
      setLoading(false);
    }
  };

  // Generate QR Code from token
  useEffect(() => {
    if (token) {
      QRCode.toDataURL(token, {
        width: 320,
        margin: 2,
        color: {
          dark: isDarkMode ? "#FFFFFF" : "#000000",
          light: isDarkMode ? "#1A1A1A" : "#FFFFFF",
        },
      })
        .then((url) => setQrCodeUrl(url))
        .catch((err) => {
          console.error("Error generating QR code", err);
        });
    }
  }, [token, isDarkMode]);

  // Initial fetch and intervals setup
  useEffect(() => {
    fetchToken();

    // Token refresh interval (every 5 minutes = 300,000ms)
    tokenIntervalRef.current = setInterval(() => {
      fetchToken();
    }, 300000);

    // Countdown interval (every second)
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 300;
        }
        return prev - 1;
      });
    }, 1000);

    // Clock update interval (every second)
    clockIntervalRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Fullscreen event listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      if (tokenIntervalRef.current) clearInterval(tokenIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (clockIntervalRef.current) clearInterval(clockIntervalRef.current);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Error enabling fullscreen", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const themeColors = {
    bg: isDarkMode ? "#121212" : "#F5F5F7",
    cardBg: isDarkMode ? "#1A1A1A" : "#FFFFFF",
    textPrimary: isDarkMode ? "#FFFFFF" : "#1D1D1F",
    textSecondary: isDarkMode ? "#A1A1A6" : "#86868B",
    borderColor: isDarkMode ? "#333333" : "#E5E5E7",
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        minHeight: isFullscreen ? "100vh" : "500px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: themeColors.bg,
        color: themeColors.textPrimary,
        p: 4,
        position: "relative",
        transition: "all 0.3s ease",
      }}
    >
      {/* Control Buttons */}
      <Box sx={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 1 }}>
        <Tooltip title={t("toggle_theme") || "Toggle Light/Dark Theme"}>
          <IconButton
            onClick={() => setIsDarkMode(!isDarkMode)}
            sx={{ color: themeColors.textSecondary }}
          >
            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={t("refresh_token") || "Force Refresh QR Code"}>
          <IconButton onClick={fetchToken} sx={{ color: themeColors.textSecondary }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isFullscreen ? t("exit_fullscreen") : t("fullscreen")}>
          <IconButton onClick={handleToggleFullscreen} sx={{ color: themeColors.textSecondary }}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Info Tooltip */}
      <Box sx={{ position: "absolute", top: 16, left: 16 }}>
        <Tooltip
          title={
            t("qr_code_explanation") ||
            "Teachers can scan this QR code using their check-in screen if GPS location is unavailable or inaccurate."
          }
          placement="bottom-start"
        >
          <IconButton sx={{ color: themeColors.textSecondary }}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          bgcolor: themeColors.cardBg,
          color: themeColors.textPrimary,
          border: `1px solid ${themeColors.borderColor}`,
          borderRadius: 4,
          maxWidth: "450px",
          width: "100%",
          boxShadow: isDarkMode
            ? "0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            : "0 8px 32px 0 rgba(0, 0, 0, 0.08)",
        }}
      >
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          {t("Staffroom QR Check-In") || "Staffroom Attendance"}
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.textSecondary, mb: 3 }}>
          {t("Scan this QR code to check in / check out")}
        </Typography>

        {/* QR Code Frame */}
        <Box
          sx={{
            width: "320px",
            height: "320px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
            border: `1px solid ${themeColors.borderColor}`,
            borderRadius: 2,
            overflow: "hidden",
            mb: 3,
          }}
        >
          {loading ? (
            <CircularProgress color="primary" />
          ) : error ? (
            <Typography variant="body2" color="error" textAlign="center" sx={{ px: 2 }}>
              {error}
            </Typography>
          ) : (
            qrCodeUrl && (
              <Box
                component="img"
                src={qrCodeUrl}
                alt="Rotating Attendance QR Code"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            )
          )}
        </Box>

        {/* Live Clock Display */}
        <Typography variant="h3" fontWeight="bold" sx={{ fontFamily: "monospace", mb: 0.5 }}>
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.textSecondary, mb: 3 }}>
          {formatDate(currentTime)}
        </Typography>

        {/* Countdown Timer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
            {t("QR code updates in:")}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="primary"
            sx={{ fontFamily: "monospace" }}
          >
            {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, "0")}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default QrCodeDisplay;
